import React, { useEffect, useRef } from "react";
import { Box, styled } from "@mui/material";
import { GoogleMap } from "types/map";
import useMapRenderer from "hooks/maps/useMapRenderer";

const MapBoundaries = styled(Box)``;

/*
 * SDK MapOptions documentation:
 *
 * https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
 */
interface Props extends google.maps.MapOptions {
  boundaryWidth: "100%" | "100vw";
  boundaryHeight: "100%" | "100vh";
  map: google.maps.Map | null;
  onMapInitialized: (googleMap: GoogleMap) => void;
  onClick?: (event: google.maps.MapMouseEvent) => void;
  onIdle?: (map: GoogleMap) => void;
  children: React.ReactElement;
  suppressDirectionMarkers?: boolean;
}

const MapContainer = ({
  children,
  boundaryWidth,
  boundaryHeight,
  map,
  onClick,
  onIdle,
  onMapInitialized,
  suppressDirectionMarkers = false,
  ...googleMapOptions
}: Props) => {
  const wrapperReference = useRef<HTMLDivElement | null>(null);

  useMapRenderer({
    onMapInitialized,
    mapWrapperReference: wrapperReference,
    googleMapOptions,
    suppressDirectionMarkers,
  });

  // Register det events "click" and "idle"
  // Full list: https://developers.google.com/maps/documentation/javascript/events
  useEffect(() => {
    map && google.maps.event.clearListeners(map, "click");
    map && google.maps.event.clearListeners(map, "idle");
    map && onClick && map.addListener("click", onClick);
    map && onIdle && map.addListener("idle", () => onIdle(map));
  }, [map, onClick, onIdle]);

  return (
    <>
      <MapBoundaries
        width={boundaryWidth}
        height={boundaryHeight}
        ref={wrapperReference}
      />
      {children}
    </>
  );
};

export default MapContainer;
